import React, { useState, useEffect } from 'react';
import { Button, message, Tooltip } from 'antd';
import { CopyOutlined, KeyOutlined, PlusOutlined } from '@ant-design/icons';
import { generateApiKey, fetchBrandApiKeys } from '../api/Api';
import { useAspDispatch } from '../../../test/jest-redux-hooks';

const ApiKeys = ({ brandId }) => {
  const [apiKeys, setApiKeys] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    loadApiKeys();
  }, [brandId]);

  const loadApiKeys = async () => {
    try {
      setIsLoading(true);
      const response = await fetchBrandApiKeys(brandId);
      setApiKeys(response || []);
    } catch (error) {
      console.error('Error loading API keys:', error);
      messageApi.error('Failed to load API keys');
    } finally {
      setIsLoading(false);
    }
  };

  const showMessage = (content, type = 'success') => {
    // messageApi[type](content);
  };

  const handleGenerateKey = async () => {
    if (isGenerating) return;
    try {
      setIsGenerating(true);
      await generateApiKey({ brand_id: brandId });
      await loadApiKeys();
      showMessage('API key generated successfully');
    } catch (error) {
      console.error('Error generating API key:', error);
      showMessage('Failed to generate API key', 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      showMessage('API key copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      showMessage('Failed to copy API key', 'error');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const key = apiKeys[0]; // Assuming only one key

  return (
    <div className="max-w-8xl mx-auto">
      {contextHolder}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <KeyOutlined className="text-gray-700 mr-3" />
              <h2 className="text-lg font-medium text-gray-900">API Keys</h2>
            </div>
            {/* {!key && (
              <Button
                type="primary"
                icon={<KeyOutlined />}
                onClick={handleGenerateKey}
                loading={isGenerating}
                className="bg-[#05805c] hover:!bg-[#05805c] border-purple-600 hover:border-purple-700"
              >
                Generate New Key
              </Button>
            )} */}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* API Key List */}
          <div className="mb-6">
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-700">API Keys</h3>
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : apiKeys.length > 0 ? (
              <div className="space-y-4">
                {apiKeys.map((key) => (
                  <div
                    key={key.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center mb-2">
                          <span className="text-sm font-medium text-gray-900">
                            Status
                          </span>
                          <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-green-50 text-green-700 border border-green-100">
                            Active
                          </span>
                        </div>
                        <div className="bg-gray-50 p-4 rounded border border-gray-200 break-all font-mono text-sm text-gray-700">
                          {key.api_key}
                        </div>
                      </div>
                      <div className="mt-4 md:mt-0 md:ml-4 text-sm text-right">
                        <div className="text-gray-500">
                          Created on {formatDate(key.created_at)}
                        </div>
                        {key.last_used_at && (
                          <div className="text-gray-500">
                            Last used {formatDate(key.last_used_at)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
                <KeyOutlined className="text-4xl text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No API keys found
                </h3>
                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                  You don't have any API keys yet. Create your first key to get
                  started.
                </p>
                <Button
                  type="primary"
                  icon={<KeyOutlined />}
                  onClick={handleGenerateKey}
                  loading={isGenerating}
                  className="bg-[#05805c] hover:!bg-[#05805c] border-purple-600 hover:border-purple-700"
                >
                  Generate API Key
                </Button>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
          {key && (
            <Button
              type="primary"
              icon={<KeyOutlined />}
              onClick={handleGenerateKey}
              loading={isGenerating}
              className="bg-[#05805c] hover:!bg-[#05805c] border-purple-600 hover:border-purple-700"
            >
              {key ? 'Regenerate Key' : 'Generate New Key'}
            </Button>
          )}
          {key?.api_key && (
              <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(key.api_key)}
                  className="border-gray-300 hover:bg-gray-50"
                >
                  Copy API Key
                </Button>
              </Tooltip>
            )}
          </div>

          {/* API Key Usage Notes */}
          <div className="mt-8 space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
              <h3 className="text-sm font-medium text-gray-900 mb-2">
                How to use your API key
              </h3>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                <li>
                  The{' '}
                  <code className="bg-gray-100 px-1.5 py-0.5 rounded">
                    X-API-KEY
                  </code>{' '}
                  is required to access GainWix API endpoints
                </li>
                <li>
                  Include the{' '}
                  <code className="bg-gray-100 px-1.5 py-0.5 rounded">
                    X-API-KEY
                  </code>{' '}
                  in the header of all API requests
                </li>
                <li>
                  Example:{' '}
                  <code className="bg-gray-100 px-1.5 py-0.5 rounded">
                    X-API-KEY: {key?.api_key || 'your_api_key_here'}
                  </code>
                </li>
              </ul>
            </div>
          </div>

          {/* Help Section */}
          <div className="bg-gray-50 p-4 rounded-lg mt-6">
            <h3 className="text-sm font-medium text-gray-900 mb-3">
              Need help with API keys?
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Learn how to use API keys to authenticate with our API and start
              building integrations.
            </p>
            <a
              href="https://documenter.getpostman.com/view/38419539/2sAYkKKJbJ"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-purple-600 hover:text-purple-500"
            >
              View API documentation →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiKeys;
